import { Stack } from '@mui/material';
import { DateFormat, formatDate, getDateInMoment } from 'src/compass/shared/util/casMoment';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';

interface IProps extends ICasRenderCellProps {
  // Frequency lookup: { id: Days, name: Description }. Passed in rather than fetched
  // here so one row's cell doesn't open its own query - the tab already holds it.
  freqOptions: any[];
}

export function RenderLastTouchpoint({ row, freqOptions }: IProps) {
  const lastDate = row?.lastTouchpointDate;
  const nextDate = row?.nextTouchpointDate;

  // Overdue once the next touchpoint date has arrived or passed.
  // Both ends clamped to start-of-day so this is a whole-day delta.
  const daysOverdue = nextDate
    ? getDateInMoment().startOf('day').diff(getDateInMoment(nextDate).startOf('day'), 'days')
    : null;
  const overdueLine =
    daysOverdue !== null && daysOverdue >= 0 ? (
      <span style={{ color: '#d32f2f', fontWeight: 600 }}>
        {daysOverdue === 0
          ? 'Due today'
          : `${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'} overdue`}
      </span>
    ) : null;

  if (lastDate) {
    // Days ago = today - lastTouchpointDate. Both clamped to start-of-day so the diff
    // reflects whole-day deltas, not hours, regardless of when "today" is read.
    const daysAgo = getDateInMoment()
      .startOf('day')
      .diff(getDateInMoment(lastDate).startOf('day'), 'days');
    // Frequency Description straight from the lookup ("30 days", "Exclude"), so the
    // label always matches the Frequency select. Omitted when none is assigned.
    const freqName = freqOptions.find((o: any) => o.id === row?.touchpointFrequency)?.name;
    return (
      <Stack spacing={0}>
        {overdueLine}
        <span>{`${daysAgo} ${daysAgo === 1 ? 'day' : 'days'} ago`}</span>
        <span style={{ fontSize: '0.65rem', color: '#aaaaaa' }}>
          {formatDate(lastDate, DateFormat.M_D_YY)}
          {freqName ? `, Freq=${freqName}` : ''}
        </span>
      </Stack>
    );
  }

  // No last touchpoint recorded - if this entity has the "0 Days" frequency (i.e.
  // excluded from touchpoints), surface that frequency's Description in gray so the
  // empty cell isn't ambiguous.
  if (row?.touchpointFrequency === 0) {
    const desc = freqOptions.find((o: any) => o.id === 0)?.name;
    return desc ? <span style={{ color: 'gray' }}>{desc}</span> : null;
  }

  // Never touched but already due - still surface the overdue state.
  return overdueLine;
}
